import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMobileImmersiveEnabled, useSetMobilePanelScrollEl } from './MobileHeaderContext';

interface SwipeTabViewsProps {
  /** Active tab index (controlled — pair with the header tabs' value). */
  index: number;
  /** Called when a swipe commits to another tab. */
  onChange: (index: number) => void;
  /** One panel per tab, in tab order. */
  children: ReactNode[];
  /**
   * Freeze the pager. An open chat sets this so a horizontal swipe cannot
   * slide the Notifications panel in from under it.
   */
  locked?: boolean;
}

/**
 * Broadcast of the pager's fractional position, consumed by the header tabs so
 * their indicator tracks the drag. `position` is a fractional tab index (e.g.
 * 0.4 = 40% of the way from tab 0 to tab 1); `animate` is false while the
 * finger is down (1:1 tracking) and true for the settle/commit slide.
 */
export const TAB_SWIPE_EVENT = 'ug:tab-swipe';
export interface TabSwipeDetail {
  position: number;
  animate: boolean;
}

const emitTabSwipe = (position: number, animate: boolean): void => {
  window.dispatchEvent(
    new CustomEvent<TabSwipeDetail>(TAB_SWIPE_EVENT, { detail: { position, animate } })
  );
};

/** Left-edge strip reserved for the drawer's swipe-to-open. */
const EDGE_GUARD = 24;
/** Movement (px) before the gesture locks to an axis. */
const LOCK_THRESHOLD = 8;
/** Fraction of width dragged that commits a tab change on release. */
const COMMIT_FRACTION = 0.3;
/** Flick velocity (px/ms) that commits regardless of distance. */
const COMMIT_VELOCITY = 0.35;
/** Rubber-band factor when dragging past the first/last tab. */
const EDGE_RESISTANCE = 0.3;

/** True when the touch began inside something that owns horizontal gestures. */
const startsInHorizontalSurface = (start: EventTarget | null, stopAt: HTMLElement): boolean => {
  let el = start instanceof HTMLElement ? start : null;
  while (el && el !== stopAt) {
    if (el.tagName === 'VIDEO' || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
    const style = window.getComputedStyle(el);
    const ox = style.overflowX;
    if ((ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth) return true;
    el = el.parentElement;
  }
  return false;
};

/**
 * Native-style swipeable tab pager. Panels sit side-by-side on a horizontal
 * track that follows the finger 1:1 (direction-locked so vertical scrolling is
 * untouched), then snaps on release by distance or flick velocity.
 *
 * Each panel is its own vertical scroll container, so tabs keep independent
 * scroll positions. The active panel is published to the mobile chrome, which
 * switches into "panel mode" (overlay glass header, hide-on-scroll follows the
 * panel). Panels use scroll-container padding so content clears the overlaid
 * header / bottom nav but still scrolls behind the glass.
 *
 * Loading is lazy with keep-alive: only the active tab mounts initially,
 * neighbors preload on idle (or instantly at touch-down), and once mounted a
 * panel stays mounted — swiping back is instant and refetch-free.
 */
const SwipeTabViews: FC<SwipeTabViewsProps> = ({ index, onChange, children, locked = false }) => {
  const theme = useTheme();
  const count = children.length;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const setPanelScrollEl = useSetMobilePanelScrollEl();
  // In immersive mode there is no header and no bottom nav, so reserving space
  // for them would leave a dead band above and below the panel.
  const immersive = useMobileImmersiveEnabled();

  // Keep-alive mount set: a panel renders once visited/preloaded, then stays.
  const [mounted, setMounted] = useState<Set<number>>(() => new Set([index]));
  const mountNeighbors = useCallback(
    (around: number) => {
      setMounted((prev) => {
        const next = new Set(prev);
        next.add(around);
        if (around > 0) next.add(around - 1);
        if (around < count - 1) next.add(around + 1);
        return next.size === prev.size ? prev : next;
      });
    },
    [count]
  );

  // Idle-preload neighbors after the active tab settles, so the first swipe
  // slides real content in instead of a spinner.
  useEffect(() => {
    setMounted((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
    const idle: (cb: () => void) => number =
      (window as any).requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 300));
    const cancel: (id: number) => void =
      (window as any).cancelIdleCallback || ((id: number) => window.clearTimeout(id));
    const handle = idle(() => mountNeighbors(index));
    return () => cancel(handle);
  }, [index, mountNeighbors]);

  // Publish the active panel's scroller to the chrome (hide-on-scroll target,
  // panel-mode switch). Cleared on unmount so other pages get normal chrome.
  useEffect(() => {
    setPanelScrollEl(panelRefs.current[index] ?? null);
    return () => setPanelScrollEl(null);
  }, [index, setPanelScrollEl, mounted]);

  // Latest values for the gesture handlers (bound once).
  const indexRef = useRef(index);
  indexRef.current = index;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const countRef = useRef(count);
  countRef.current = count;
  // Read through a ref like the others: the gesture listeners are registered
  // once, so a value captured in that closure would go stale on toggle.
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  const setTrackTransform = useCallback((offsetPx: number, animate: boolean) => {
    const track = trackRef.current;
    const root = rootRef.current;
    if (!track || !root) return;
    track.style.transition = animate ? 'transform 250ms cubic-bezier(0.2, 0, 0, 1)' : 'none';
    track.style.transform = `translateX(calc(${-indexRef.current * 100}% + ${offsetPx}px))`;
  }, []);

  // Snap the track whenever the controlled index changes (tab tap or commit).
  useEffect(() => {
    setTrackTransform(0, true);
  }, [index, setTrackTransform]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let startX = 0;
    let startY = 0;
    let axis: 'x' | 'y' | null = null;
    let active = false;
    let dx = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let raf = 0;

    const onTouchStart = (e: TouchEvent) => {
      // Locked: never claim the gesture, so vertical scrolling inside the panel
      // still works and only the tab-change is suppressed.
      if (lockedRef.current || e.touches.length !== 1) {
        active = false;
        return;
      }
      const t = e.touches[0];
      active =
        t.clientX > EDGE_GUARD && !startsInHorizontalSurface(e.target, root);
      if (!active) return;
      startX = lastX = t.clientX;
      startY = t.clientY;
      lastT = e.timeStamp;
      axis = null;
      dx = 0;
      velocity = 0;
      // Make sure both neighbors are mounted before any sliding is visible.
      mountNeighbors(indexRef.current);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!active) return;
      const t = e.touches[0];
      const totalX = t.clientX - startX;
      const totalY = t.clientY - startY;

      if (!axis) {
        if (Math.abs(totalX) < LOCK_THRESHOLD && Math.abs(totalY) < LOCK_THRESHOLD) return;
        axis = Math.abs(totalX) > Math.abs(totalY) ? 'x' : 'y';
        if (axis === 'y') {
          active = false; // vertical: hand the gesture back to normal scrolling
          return;
        }
      }

      // Horizontal drag owns the gesture: stop the page from scrolling.
      e.preventDefault();

      const dt = e.timeStamp - lastT;
      if (dt > 0) velocity = (t.clientX - lastX) / dt;
      lastX = t.clientX;
      lastT = e.timeStamp;

      dx = totalX;
      const i = indexRef.current;
      // Rubber-band when dragging past the ends.
      if ((i === 0 && dx > 0) || (i === countRef.current - 1 && dx < 0)) {
        dx *= EDGE_RESISTANCE;
      }
      if (!raf) {
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          setTrackTransform(dx, false);
          // Mirror the drag into the header tabs' indicator.
          const width = root.clientWidth || 1;
          const pos = Math.min(
            countRef.current - 1,
            Math.max(0, indexRef.current - dx / width)
          );
          emitTabSwipe(pos, false);
        });
      }
    };

    const settle = () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
      if (!active || axis !== 'x') {
        active = false;
        return;
      }
      active = false;
      const width = root.clientWidth || 1;
      const i = indexRef.current;
      let target = i;
      const passedDistance = Math.abs(dx) > width * COMMIT_FRACTION;
      const flicked = Math.abs(velocity) > COMMIT_VELOCITY && Math.sign(velocity) === Math.sign(dx);
      if ((passedDistance || flicked) && dx !== 0) {
        target = dx < 0 ? i + 1 : i - 1;
      }
      if (target < 0 || target > countRef.current - 1 || target === i) {
        setTrackTransform(0, true); // spring back
        emitTabSwipe(i, true);
      } else {
        onChangeRef.current(target); // index effect snaps the track
        emitTabSwipe(target, true);
      }
      dx = 0;
    };

    root.addEventListener('touchstart', onTouchStart, { passive: true });
    // touchmove must be non-passive so a locked horizontal drag can block scroll.
    root.addEventListener('touchmove', onTouchMove, { passive: false });
    root.addEventListener('touchend', settle, { passive: true });
    root.addEventListener('touchcancel', settle, { passive: true });

    return () => {
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
      root.removeEventListener('touchend', settle);
      root.removeEventListener('touchcancel', settle);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [mountNeighbors, setTrackTransform]);

  const panelSx = useMemo(
    () => ({
      flex: '0 0 100%',
      width: '100%',
      height: '100%',
      minWidth: 0,
      overflowY: 'auto' as const,
      overflowX: 'hidden' as const,
      // Scroll-container padding: content clears the overlaid glass chrome but
      // still scrolls behind it (top header / bottom nav). Immersive panels have
      // no chrome to clear.
      pt: immersive ? 0 : 'var(--mobile-header-h, 0px)',
      pb: immersive ? 0 : theme.tokens.layout.bottomNavSafePadding,
    }),
    [theme, immersive]
  );

  return (
    <Box
      ref={rootRef}
      sx={{ flex: 1, minHeight: 0, width: '100%', height: '100%', overflow: 'hidden' }}
    >
      <Box
        ref={trackRef}
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          transform: `translateX(${-index * 100}%)`,
        }}
      >
        {children.map((child, i) => (
          <Box
            key={i}
            ref={(el: HTMLDivElement | null) => {
              panelRefs.current[i] = el;
            }}
            sx={panelSx}
          >
            {mounted.has(i) ? child : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SwipeTabViews;
